/**
 * One list position in the day, made moveable — the drag strips, the drop edges around it, and the
 * keyboard lift on it.
 *
 * ── The unit is the BLOCK, which is the canon's own reading ──────────────────────────────────
 * "jeden numer pozycji, szyna spinająca dwa wiersze i podpis »Superset«" — a superset is ONE
 * programming decision, so one lift carries both members and they land together. Mobile reorders at
 * exactly this level too (`DayBody.handleReorder` moves blocks and flattens afterwards), so the two
 * clients cannot disagree about what a drag moved.
 *
 * ── There is no grip, and the keyboard lift starts from the ⋯ (owner, 15 September 2026) ────
 * The row drew the canon's six dots until the owner took them away: the whole head already drags,
 * so the dots only said what the cursor says. The KEYBOARD path they carried is kept, and it now
 * starts from the ⋯'s "Przestaw" entry: the menu closes onto this block's own row, which holds the
 * lift — arrows aim, Space or Enter drops, Escape or Tab cancels. View ADR §12, amended that day.
 *
 * ── Nothing is written until the drop ────────────────────────────────────────────────────────
 * The keyboard lift holds a pending POSITION here and dispatches once, on Space. Escape drops it
 * with no store operation at all — no patch, no undo entry, no dirty flag. See `useKeyboardMove`.
 */

import { useRef, type ReactNode } from 'react';

import { useDraggableItem } from '~/shared/ui/dnd/useDraggableItem';
import { useDropTarget } from '~/shared/ui/dnd/useDropTarget';
import { useKeyboardMove } from '~/shared/ui/dnd/useKeyboardMove';
import type { MenuLift } from '~/shared/ui/dnd/useMenuLift';

import styles from './MoveableBlock.module.css';

export interface BlockMoveProps {
  /** The id of the element carrying the keyboard instruction, once for the whole dock. */
  readonly hintId: string;
  /** The ⋯ entry that starts the lift, worded by `exerciseLiftEntry`. */
  readonly liftEntry: { readonly label: string; readonly disabledReason: string | null };
  /** "Podniesiono X. Pozycja 3 z 5. Strzałki…" — said once, on pickup. */
  readonly liftedWords: (position: number, total: number) => string;
  /** "Pozycja 3 z 5." for each subsequent keyboard step. Worded by the feature's model. */
  readonly positionWords: (position: number, total: number) => string;
  readonly cancelledMessage: string;
  readonly announce: (message: string) => void;
  readonly moveBlock: (
    exerciseSlotToken: string,
    targetDaySlotToken: string,
    targetBlockIndex: number,
    name: string,
  ) => void;
  readonly setPending: (
    pending: { readonly blockToken: string; readonly position: number } | null,
  ) => void;
}

/**
 * What one strip needs in order to BE the block's drag source. Opaque to the card that places it —
 * it carries the binding through and never opens it.
 */
export interface DragStripBinding {
  /** What the drop reads. The BLOCK's address, so every strip of one block reports the same move. */
  readonly payload: Record<string, unknown>;
  readonly canDrag: boolean;
}

/**
 * Everything a card needs to become the block's drag source: the binding for every strip the gesture
 * may start from, and the lift its ⋯ starts.
 *
 * Handed DOWN rather than the elements handed up: one module owns the mechanic, the other owns where
 * it is drawn.
 */
export interface BlockDragSource {
  /** `null` on a card that is not the block's first — a superset's second member. */
  readonly lift: MenuLift | null;
  readonly strip: DragStripBinding;
}

/**
 * One strip of a block, made the source of the block's drag.
 *
 * ── Why this is a COMPONENT and not a ref handed to the card ─────────────────────────────────
 * A block has as many strips as its drawing needs: the card's head, the `Grupa` bar over a prep
 * box, and the head of every member of a superset — the mock marks all of them `draggable` and they
 * all move the same block. One ref covers one element, so the number of sources would have become a
 * number of props. A component binds each element where it stands, and the count stops mattering.
 *
 * `binding` is `null` where the block may not be moved — a viewer, or a card that is not the block's
 * address. A consumed phase was the third case until 15 September 2026, when the live locks were
 * withdrawn: the backend refuses no move inside a day. The element is drawn either way, so no
 * caller branches its own layout on whether a drag exists.
 */
export function DragStrip({
  binding,
  className,
  children,
}: {
  readonly binding: DragStripBinding | null;
  readonly className: string;
  readonly children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useDraggableItem({
    ref,
    data: binding?.payload ?? {},
    canDrag: binding !== null && binding.canDrag,
  });

  return (
    <div
      ref={ref}
      className={className}
      /* The `cursor: grab` hook, and the thing the block's dimming reads. Both belong to the strip
       * that actually carries the gesture, so a strip that does not is neither. */
      data-drag={binding !== null && binding.canDrag ? '' : undefined}
      data-dragging={drag.isDragging ? '' : undefined}
    >
      {children}
    </div>
  );
}

interface MoveableBlockProps {
  readonly daySlotToken: string;
  /** The block's own position among the day's main-work blocks. */
  readonly index: number;
  readonly blockCount: number;
  /** The first member's slot token — the block's address, and what a command names. */
  readonly leadSlotToken: string;
  /** The block's name, for the announcement. A superset takes its first member's. */
  readonly name: string;
  /** "Przenieś ćwiczenie X" — the name the row takes while it holds a lift. */
  readonly liftLabel: string;
  readonly move: BlockMoveProps | null;
  /** True when a keyboard lift in this day is pointed at this block's position. */
  readonly isPendingTarget: boolean;
  /**
   * The block, given its own drag source to place.
   *
   * A render prop rather than a `children` node, because the drag strips and the ⋯ that starts a
   * lift are drawn inside the card, while the drag, the drop edges and the keyboard lift all belong
   * here. `null` where nothing may be moved.
   */
  readonly children: (source: BlockDragSource | null) => ReactNode;
}

export function MoveableBlock({
  daySlotToken,
  index,
  blockCount,
  leadSlotToken,
  name,
  liftLabel,
  move,
  isPendingTarget,
  children,
}: MoveableBlockProps) {
  const canMove = move !== null;

  const itemRef = useRef<HTMLLIElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);

  /**
   * The block's STRIPS are the drag source — not a grip, and not the card.
   *
   * Not a grip, because restricting the gesture to a nine pixel square was reported twice as
   * "drag and drop does not work at all", once on the week and once here, and the grip itself went
   * on 15 September 2026.
   *
   * Not the card, because the browser's drag image is the draggable ELEMENT, so binding the card
   * made an open exercise fly as a full-height block with its set table hanging off the pointer.
   * The mock puts `draggable` on `headSt` and on `grpBar` and on nothing else, and the picture
   * either one carries is the same narrow strip whether the card below it is open or folded.
   *
   * Every strip reports THIS block, by its lead slot token: which row the trainer grabbed decides
   * nothing about what moves.
   */
  const strip: DragStripBinding = {
    payload: { kind: 'exercise', exerciseSlotToken: leadSlotToken, name },
    canDrag: canMove,
  };

  const drop = useDropTarget({
    ref: itemRef,
    data: { kind: 'blockSlot', daySlotToken, blockIndex: index },
    // Both sources land between blocks. The gate lives in the entity and is asked at the drop, not
    // here: a list item cannot refuse a library pick on grounds it has no way to evaluate.
    canDrop: (source) =>
      canMove && (source.kind === 'exercise' || source.kind === 'libraryExercise'),
    allowedEdges: ['top', 'bottom'],
  });

  const keyboard = useKeyboardMove({
    count: blockCount,
    index,
    axis: 'vertical',
    canLift: canMove,
    onLift: (target) => {
      move?.setPending({ blockToken: leadSlotToken, position: target });
      move?.announce(move.liftedWords(target, blockCount));
    },
    onPreview: (target) => {
      move?.setPending({ blockToken: leadSlotToken, position: target });
      move?.announce(move.positionWords(target, blockCount));
    },
    onCommit: (target) => {
      // A POSITION becomes a GAP here, and this is the only place it does: moving down passes under
      // the rows the block vacated, so position `p` below the block's own is gap `p + 1`.
      move?.moveBlock(
        leadSlotToken,
        daySlotToken,
        target > index ? target + 1 : target,
        name,
      );
    },
    onCancel: () => {
      move?.setPending(null);
      move?.announce(move.cancelledMessage);
    },
  });

  return (
    <li
      ref={itemRef}
      className={styles.item}
      data-edge={drop.isOver ? (drop.edge ?? undefined) : undefined}
      data-pending={isPendingTarget ? '' : undefined}
    >
      {/* The lift's host. Focusable by script only — the ⋯'s "Przestaw" closes onto it — so a day
        * gains no tab stop per block. Named where a move exists at all, and DESCRIBED only while it
        * is actually holding one: at rest the row is not a control and its arrows do nothing, so a
        * standing "arrows move, Space drops" would be an instruction for a state the trainer is not
        * in. The week card one slice over has always been the other way round; this is the row
        * matching it. */}
      <div
        ref={hostRef}
        className={styles.row}
        tabIndex={move === null ? undefined : -1}
        role={move === null ? undefined : 'group'}
        aria-label={move === null ? undefined : liftLabel}
        aria-describedby={keyboard.isLifted ? move?.hintId : undefined}
        data-lifted={keyboard.isLifted ? '' : undefined}
        onKeyDown={keyboard.host.onKeyDown}
        // A lift the trainer has tabbed away from can no longer be steered, so it is dropped and
        // said rather than left holding the list. See `useKeyboardMove`.
        onBlur={keyboard.host.onBlur}
      >
        <div className={styles.body}>
          {children(
            move === null
              ? null
              : {
                  strip,
                  lift: {
                    label: move.liftEntry.label,
                    disabledReason: move.liftEntry.disabledReason,
                    host: hostRef,
                    start: keyboard.lift,
                  },
                },
          )}
        </div>
      </div>
    </li>
  );
}
